import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Users, Bell, ArrowDown, CheckCircle2, Edit, MoreVertical, Copy, ArrowUp, Trash2, Zap } from 'lucide-react';
import type { ApprovalStage } from '@/types/approvalWorkflow';

interface CompactStageCardProps {
  stage: ApprovalStage;
  stageNumber: number;
  isLastStage: boolean;
  isFirstStage?: boolean;
  totalStages: number;
  onEdit: (stage: ApprovalStage) => void;
  onDuplicate?: (stage: ApprovalStage) => void;
  onMoveUp?: (stage: ApprovalStage) => void;
  onMoveDown?: (stage: ApprovalStage) => void;
  onDelete?: (stage: ApprovalStage) => void;
}

export function CompactStageCard({ 
  stage, 
  stageNumber, 
  isLastStage, 
  isFirstStage = false,
  totalStages,
  onEdit,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onDelete,
}: CompactStageCardProps) {
  const isOnlyStage = totalStages <= 1;
  const hasApprovers = stage.approvers && stage.approvers.length > 0;
  // Check both explicit rules array and routing configuration (onApproved/onRejected/timeout)
  const hasRules = (stage.rules && stage.rules.length > 0) || stage.routingConfigured;
  const hasNotifications = stage.notificationSettings?.enabled;
  
  // Determine if stage is fully configured (approvers are required, rules are optional but shown)
  const isConfigured = hasApprovers;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on buttons/dropdowns
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    onEdit(stage);
  };

  return (
    <div className="relative">
      <Card 
        className="p-4 hover:border-primary/50 transition-colors cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex items-start gap-3">
          {/* Simple Number Badge */}
          <div className="flex-shrink-0 relative">
            <Badge variant="outline" className="w-8 h-8 rounded-full flex items-center justify-center font-semibold">
              {stageNumber}
            </Badge>
            {/* Status Indicator */}
            {isConfigured && (
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-500 border-2 border-background flex items-center justify-center">
                <CheckCircle2 className="h-2.5 w-2.5 text-white" />
              </div>
            )}
            {!isConfigured && (
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-500 border-2 border-background flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">!</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0 mr-2">
                <h3 className="font-semibold text-base mb-0.5 truncate">{stage.name}</h3>
                {stage.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{stage.description}</p>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(stage);
                  }}
                  className="h-8 w-8 p-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      className="h-8 w-8 p-0"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      onEdit(stage);
                    }}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Stage
                    </DropdownMenuItem>
                    {onDuplicate && (
                      <DropdownMenuItem onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(stage);
                      }}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {onMoveUp && (
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveUp(stage);
                        }}
                        disabled={isFirstStage}
                      >
                        <ArrowUp className="h-4 w-4 mr-2" />
                        Move Up
                      </DropdownMenuItem>
                    )}
                    {onMoveDown && (
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveDown(stage);
                        }}
                        disabled={isLastStage}
                      >
                        <ArrowDown className="h-4 w-4 mr-2" />
                        Move Down
                      </DropdownMenuItem>
                    )}
                    {onDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(stage);
                          }}
                          className="text-destructive"
                          disabled={isOnlyStage}
                          title={isOnlyStage ? 'Workflow must have at least one stage' : undefined}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Stage
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Inline Metrics - No Colored Boxes */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {hasApprovers ? (
                  <>
                    {stage.approvers.length} approver{stage.approvers.length !== 1 ? 's' : ''}
                  </>
                ) : (
                  <span className="text-amber-600">No approvers</span>
                )}
              </span>
              {hasRules && (
                <span className="flex items-center gap-1">
                  <Zap className="h-3 w-3" />
                  {stage.rules?.length || 0} rule{(stage.rules?.length || 0) !== 1 ? 's' : ''}
                </span>
              )}
              {hasNotifications && (
                <span className="flex items-center gap-1">
                  <Bell className="h-3 w-3" />
                  Notifications
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Flow Indicator */}
      {!isLastStage && (
        <div className="flex items-center justify-center py-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="h-px w-8 bg-border" />
            <ArrowDown className="h-4 w-4" />
            <div className="h-px w-8 bg-border" />
          </div>
        </div>
      )}
    </div>
  );
}
